import { alpha, Box, Chip, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import type { Result } from '../../types/results';
import type { Team } from '../../types/domain';
import type { TimeFormat } from '../../types/domain';
import { RankBadge } from './RankBadge';
import { AttemptsDetail } from './AttemptsDetail';
import { bestAttemptPerTeam } from './bestAttempt';
import { formatCzechDateTime } from '../../utils/formatDateTime';

interface ResultsTableProps {
  results: Result[];
  highlightTeamId?: number | null;
  showAttempt?: boolean;
  showStartNumber?: boolean;
  showAttemptDetails?: boolean;
  showLaneDetails?: boolean;
  onHistoryClick?: (resultId: number) => void;
  /** Pokud jsou pass-nuty a je aktivni showAllTeams, pridaji se tymy bez vysledku. */
  teamsInCategory?: Team[];
  showAllTeams?: boolean;
  /** 'RANK' razeni podle poradi (default pro dokoncene), 'START_NUMBER' podle startovniho cisla. */
  sortMode?: 'RANK' | 'START_NUMBER';
  /** Format casu z disciplíny — pouziva se pro detaily pokusu/drah. */
  timeFormat?: TimeFormat;
  /** Pokud true, zobrazi sloupec "Ukoncen" + tlacitko pro upravu (jen UNIT_ADMIN+). */
  showCompletedAt?: boolean;
  /** Callback po kliknuti na tlacitko upravy — predava resultId + initial completedAt. */
  onEditCompletedAt?: (resultId: number, completedAt: string | null) => void;
}

type Row =
  | { kind: 'result'; result: Result }
  | { kind: 'missing'; teamId: number; teamName: string; teamStartNumber: number };

export const ResultsTable = ({
  results,
  highlightTeamId,
  showAttempt,
  showStartNumber = true,
  showAttemptDetails = false,
  showLaneDetails = false,
  onHistoryClick,
  teamsInCategory,
  showAllTeams = false,
  sortMode = 'RANK',
  timeFormat,
  showCompletedAt = false,
  onEditCompletedAt,
}: ResultsTableProps) => {
  const theme = useTheme();
  const detailsEnabled = showAttemptDetails || showLaneDetails;
  const deduped = bestAttemptPerTeam(results);

  const rows: Row[] = deduped.map((r) => ({ kind: 'result', result: r }));
  if (showAllTeams && teamsInCategory) {
    const resultTeamIds = new Set(deduped.map((r) => r.teamId));
    teamsInCategory
      .filter((t) => !resultTeamIds.has(t.id))
      .forEach((t) =>
        rows.push({
          kind: 'missing',
          teamId: t.id,
          teamName: t.name,
          teamStartNumber: t.startNumber,
        }),
      );
  }

  const getStart = (row: Row) =>
    row.kind === 'result' ? row.result.teamStartNumber : row.teamStartNumber;
  const getRank = (row: Row) => (row.kind === 'result' ? row.result.rank : null);
  const getIsNp = (row: Row) => row.kind === 'result' && row.result.isNp;

  const sorted = [...rows].sort((a, b) => {
    if (sortMode === 'START_NUMBER') return getStart(a) - getStart(b);
    // RANK
    const ra = getRank(a);
    const rb = getRank(b);
    if (ra != null && rb != null) {
      const byRank = ra - rb;
      return byRank !== 0 ? byRank : getStart(a) - getStart(b);
    }
    if (ra != null) return -1;
    if (rb != null) return 1;
    const na = getIsNp(a);
    const nb = getIsNp(b);
    if (na && !nb) return -1;
    if (!na && nb) return 1;
    return getStart(a) - getStart(b);
  });

  const headerSx = {
    fontWeight: 700,
    fontSize: '0.72rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: 'text.secondary',
    borderBottom: `2px solid ${theme.palette.divider}`,
    py: 1.25,
    px: { xs: 0.75, sm: 2 },
  };

  const startBadgeSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 26,
    height: 26,
    px: 0.5,
    borderRadius: '999px',
    bgcolor: alpha(theme.palette.text.primary, 0.06),
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'text.primary',
  };

  return (
    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 0, tableLayout: 'auto' }}>
        <TableHead>
          <TableRow>
            {showStartNumber && <TableCell sx={{ ...headerSx, width: { xs: 36, sm: 56 } }}>Č.</TableCell>}
            <TableCell sx={headerSx}>Družstvo</TableCell>
            {showAttempt && <TableCell sx={{ ...headerSx, width: 72 }}>Pokus</TableCell>}
            <TableCell align="right" sx={headerSx}>Čas</TableCell>
            <TableCell align="center" sx={{ ...headerSx, width: { xs: 48, sm: 64 } }}>Poř.</TableCell>
            {showCompletedAt && (
              <TableCell align="center" sx={{ ...headerSx, minWidth: 170 }}>Ukončen</TableCell>
            )}
            {onHistoryClick && <TableCell sx={{ ...headerSx, width: 40 }} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row) => {
            if (row.kind === 'missing') {
              const isHighlight = highlightTeamId === row.teamId;
              return (
                <TableRow
                  key={`missing-${row.teamId}`}
                  sx={{
                    bgcolor: isHighlight ? alpha(theme.palette.primary.main, 0.12) : undefined,
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                    '& td': { py: 1.25, px: { xs: 0.75, sm: 2 } },
                  }}
                >
                  {showStartNumber && (
                    <TableCell>
                      <Box sx={{ ...startBadgeSx, bgcolor: alpha(theme.palette.text.primary, 0.04), color: 'text.disabled' }}>
                        {row.teamStartNumber}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography
                      fontWeight={isHighlight ? 700 : 500}
                      color="text.disabled"
                      sx={{ fontStyle: 'italic' }}
                    >
                      {row.teamName}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      Čeká na start
                    </Typography>
                  </TableCell>
                  {showAttempt && <TableCell />}
                  <TableCell align="right">
                    <Typography color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                      —
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <RankBadge rank={null} size={28} />
                  </TableCell>
                  {showCompletedAt && (
                    <TableCell align="center">
                      <Typography color="text.disabled" variant="caption">—</Typography>
                    </TableCell>
                  )}
                  {onHistoryClick && <TableCell />}
                </TableRow>
              );
            }
            const r = row.result;
            const isHighlight = highlightTeamId === r.teamId;
            return (
              <TableRow
                key={r.id}
                sx={{
                  bgcolor: isHighlight ? alpha(theme.palette.primary.main, 0.12) : undefined,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                  '& td': { py: 1.25, px: { xs: 0.75, sm: 2 } },
                  transition: 'background-color 120ms ease',
                }}
              >
                {showStartNumber && (
                  <TableCell>
                    <Box sx={startBadgeSx}>{r.teamStartNumber}</Box>
                  </TableCell>
                )}
                <TableCell>
                  <Typography
                    fontWeight={isHighlight ? 700 : 500}
                    sx={{ fontSize: '0.95rem' }}
                  >
                    {r.teamName}
                  </Typography>
                  {detailsEnabled && (
                    <AttemptsDetail
                      attempts={r.attempts}
                      showAttemptDetails={showAttemptDetails}
                      showLaneDetails={showLaneDetails}
                      compact
                      disableTooltip
                      timeFormat={timeFormat}
                    />
                  )}
                </TableCell>
                {showAttempt && <TableCell>{r.attemptNumber}.</TableCell>}
                <TableCell align="right">
                  {r.isNp ? (
                    <Chip size="small" label="NP" color="error" variant="outlined" />
                  ) : r.timeFinalMs != null ? (
                    <Typography
                      fontFamily="monospace"
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.05rem',
                        color: 'text.primary',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {r.timeFinalFormatted}
                    </Typography>
                  ) : (
                    <Typography color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" alignItems="center" justifyContent="center">
                    <RankBadge rank={r.rank} size={28} />
                  </Stack>
                </TableCell>
                {showCompletedAt && (
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {formatCzechDateTime(r.completedAt)}
                      </Typography>
                      {onEditCompletedAt && (
                        <Tooltip title="Upravit čas ukončení">
                          <IconButton size="small" onClick={() => onEditCompletedAt(r.id, r.completedAt ?? null)}>
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                )}
                {onHistoryClick && (
                  <TableCell>
                    <Tooltip title="Historie změn">
                      <IconButton size="small" onClick={() => onHistoryClick(r.id)}>
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={(showAttempt ? 1 : 0) + (showStartNumber ? 1 : 0) + 3 + (showCompletedAt ? 1 : 0) + (onHistoryClick ? 1 : 0)}>
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Typography>Zatím žádné výsledky.</Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
